/**
 * One account's page.
 *
 * The thing the official explorer does badly: what an address has actually been
 * paid, in full, back as far as the observer has read. Everything here comes
 * from this observer's own index — events decoded against the runtime that
 * produced each block — so it does not depend on any external indexer and does
 * not stop working when the node prunes the state behind it.
 *
 * The activity table renders whatever the runtime emitted, under the runtime's
 * own names. `LABELS` and `AMOUNTS` below are presentation only: a field they
 * do not know still appears, as its key and its value, rather than vanishing.
 * That is the difference between a page that shows what happened and one that
 * shows the subset somebody remembered to write a case for.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type { AccountDetail } from '../api/generated/AccountDetail'
import type { AccountEvent } from '../api/generated/AccountEvent'
import { RequestFailed, fetchAccount } from '../api/rest'
import { exactTokens, height as fmtHeight, shortAddress, tokens, when } from '../lib/format'
import { href } from '../lib/routes'
import { Payload } from './Payload'
import { RoleChips } from './RoleChips'

/** Human labels for what the site sees most. Anything else shows its own
 *  `Pallet::name`, which is already the runtime's answer and not a guess. */
const LABELS: Record<string, string> = {
  'MiningRewards::MinerRewarded': 'Mining reward',
  'MiningRewards::FeesCollected': 'Fees collected',
  'Balances::Transfer': 'Transfer',
  'Balances::transfer_allow_death': 'Transfer',
  'Balances::transfer_keep_alive': 'Transfer',
  'Utility::batch_all': 'Batch',
  'Timestamp::set': 'Timestamp',
}

function label(row: AccountEvent): string {
  return LABELS[`${row.pallet}::${row.name}`] ?? `${row.pallet}::${row.name}`
}

export function AccountPanel({
  chain,
  address,
  decimals,
  symbol,
}: {
  chain: string
  address: string
  /** The chain's token decimals; 12 on Quantus, but read rather than assumed. */
  decimals: number
  symbol: string
}) {
  const [detail, setDetail] = useState<AccountDetail | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Pulled out of the whole `System::Account` entry rather than sent as their
  // own fields: they are the runtime's names, and reading them here keeps the
  // wire format the chain's own shape.
  const st = detail?.state as Record<string, unknown> | null | undefined
  const data = (st?.data ?? null) as Record<string, unknown> | null
  const reserved = data?.reserved !== undefined ? String(data.reserved) : null
  const nonce = st?.nonce !== undefined ? String(st.nonce) : null

  useEffect(() => {
    const controller = new AbortController()
    setDetail(null)
    setError(null)
    fetchAccount(chain, address, undefined, controller.signal)
      .then(setDetail)
      .catch((e: unknown) => {
        // An abort is this component being replaced, not a failure to report.
        if (controller.signal.aborted) return
        setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
      })
    return () => controller.abort()
  }, [chain, address])

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <div>
          <div className="eyebrow">Account</div>
          {/* An address is case-sensitive — SS58's checksum is over the exact
              bytes and the base58 alphabet uses both cases — so the panel
              title's uppercase is switched off when the title *is* one. A name
              keeps it. */}
          <h2
            className="panel-title"
            style={{ marginTop: 4, textTransform: detail?.display ? undefined : 'none' }}
          >
            {detail?.display ?? shortAddress(detail?.address ?? address)}
            {detail && detail.roles.length > 0 && (
              <span className="title-chips">
                <RoleChips roles={detail.roles} />
              </span>
            )}
          </h2>
          <dl className="identifiers">
            <dt>Address</dt>
            <dd className="numeral">{detail?.address ?? address}</dd>
            {detail?.miner && (
              <>
                {/* The preimage is the identity the chain itself asserts, and
                    the one the standings are keyed on — so this is the way back
                    to the miner's own page from the address they recognise. */}
                <dt>Preimage</dt>
                <dd className="numeral">
                  <Link to={href({ chain, miner: detail.miner })}>{detail.miner}</Link>
                </dd>
              </>
            )}
          </dl>
        </div>
        {/* No close button. This is a URL somebody navigated to, not a dialog
            that opened over something they were reading — there is nothing
            underneath to get back to, and the back button and the chain chip
            both already do what a close button would. */}
      </div>

      {/* The citation. The chip beside the title is the claim; this is the
          evidence, and it is where the *kind* of specialness gets stated —
          compiled into the runtime, assigned in state, or simply held a
          balance in block zero. */}
      {detail && detail.roles.length > 0 && (
        <div className="banner banner-role">
          {detail.roles.map((r) => (
            <div key={`${r.source}-${r.cited}`}>
              {r.source === 'constant' && (
                <>
                  Named by the runtime constant <code>{r.cited}</code> — compiled in, and fixed for
                  as long as this runtime is in force.
                </>
              )}
              {r.source === 'state' && (
                <>
                  Named by <code>{r.cited}</code> in chain state — assigned, and changeable by
                  whatever call the pallet provides for it.
                </>
              )}
              {r.source === 'genesis' && (
                <>
                  Endowed at genesis
                  {r.endowment && ` with ${tokens(r.endowment, decimals, 4)} ${symbol}`}. History,
                  not a job — an account can be endowed and have no role at all.
                </>
              )}
              {r.docs && <span className="banner-doc">{r.docs}</span>}
            </div>
          ))}
        </div>
      )}

      {error && <p className="empty">{error}</p>}
      {!error && !detail && <p className="empty">Reading the record…</p>}

      {detail && (
        <>
          {/* Only for an account that has actually been paid. Four tiles
              reading 0, 0, — and — is not a summary of anything; it is the
              mining shape of this page imposed on an account that has never
              mined, and it pushes the history that does exist below the fold. */}
          {/* Held now, from state — a different number from anything summed
              below, and the one most people came for. Absent is not zero:
              Substrate reaps an empty account, so a missing entry means the
              account does not exist, and a 0 would say it had been funded and
              spent. */}
          <div className="stats" style={{ margin: 0, border: 0, borderBottom: 'var(--rule)' }}>
            <div className="stat">
              <div className="stat-label">Balance</div>
              <div
                className="stat-value"
                title={
                  detail.balance ? `${exactTokens(detail.balance, decimals)} ${symbol}` : undefined
                }
              >
                {detail.balance ? tokens(detail.balance, decimals, 4) : '—'}
              </div>
              <div className="stat-note">
                {detail.balance ? `${symbol} held now` : 'no account on chain'}
              </div>
            </div>
            {reserved !== null && reserved !== '0' && (
              <div className="stat">
                <div className="stat-label">Reserved</div>
                <div className="stat-value">{tokens(reserved, decimals, 4)}</div>
                <div className="stat-note">{symbol} in deposits</div>
              </div>
            )}
            <div className="stat">
              <div className="stat-label">Nonce</div>
              <div className="stat-value">{nonce === null ? '—' : fmtHeight(Number(nonce))}</div>
              <div className="stat-note">transactions sent</div>
            </div>
          </div>

          {detail.rewards.count > 0 && (
            <div className="stats" style={{ margin: 0, border: 0, borderBottom: 'var(--rule)' }}>
              <div className="stat">
                <div className="stat-label">Mined</div>
                <div
                  className="stat-value"
                  title={`${exactTokens(detail.rewards.total, decimals)} ${symbol}`}
                >
                  {tokens(detail.rewards.total, decimals, 2)}
                </div>
                <div className="stat-note">{symbol} in rewards</div>
              </div>
              <div className="stat">
                <div className="stat-label">Blocks paid</div>
                <div className="stat-value">{fmtHeight(detail.rewards.count)}</div>
                <div className="stat-note">reward events</div>
              </div>
              <div className="stat">
                <div className="stat-label">First paid</div>
                <div className="stat-value" style={{ fontSize: 22 }}>
                  {detail.rewards.first_height ? `#${fmtHeight(detail.rewards.first_height)}` : '—'}
                </div>
                <div className="stat-note">{when(detail.rewards.first_at)}</div>
              </div>
              <div className="stat">
                <div className="stat-label">Last paid</div>
                <div className="stat-value" style={{ fontSize: 22 }}>
                  {detail.rewards.last_height ? `#${fmtHeight(detail.rewards.last_height)}` : '—'}
                </div>
                <div className="stat-note">{when(detail.rewards.last_at)}</div>
              </div>
            </div>
          )}

          {detail.activity.length === 0 ? (
            <p className="empty">
              Nothing in the indexed record involves this account. It is a valid Quantus address —
              the checksum holds — but the observer has seen nothing happen to it.
            </p>
          ) : (
            <div className="scroll-x">
              <table className="board">
                <caption className="visually-hidden">
                  Everything the chain recorded involving this account, newest first
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Block</th>
                    <th scope="col" className="left">
                      What
                    </th>
                    <th scope="col" className="left">
                      Detail
                    </th>
                    <th scope="col" className="event-when">
                      When
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {detail.activity.map((row) => (
                    <tr key={`${row.height}-${row.source}-${row.index}`}>
                      <td className="numeral">
                        <Link to={href({ chain, block: String(row.height) })}>
                          #{fmtHeight(row.height)}
                        </Link>
                      </td>
                      <td className="left">
                        <span className="miner-cell">
                          {label(row)}
                          {/* A request and a result read differently and must
                              not look the same: an extrinsic is what somebody
                              asked for, and unlike an event it can have
                              failed. */}
                          {row.source === 'extrinsic' && (
                            <span
                              className="chip chip-submitted"
                              title={
                                row.signer === null
                                  ? 'An inherent: inserted by the block author, not submitted by anyone.'
                                  : row.signer === detail.account
                                    ? 'Submitted and signed by this account.'
                                    : 'Submitted by another account; this one is named in the call.'
                              }
                            >
                              {row.signer === null
                                ? 'inherent'
                                : row.signer === detail.account
                                  ? 'sent'
                                  : 'received'}
                            </span>
                          )}
                          {row.success === false && (
                            <span className="chip chip-failed" title="The dispatch failed.">
                              failed
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="left">
                        <Payload
                          value={row.fields}
                          ctx={{
                            chain,
                            addresses: row.addresses,
                            decimals,
                            symbol,
                            focus: detail.account,
                          }}
                        />
                      </td>
                      <td className="numeral event-when" style={{ color: 'var(--text-muted)' }}>
                        {when(row.at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* The scope of every number above. An account page is a claim about
              a range of history, and a backfill still working toward genesis
              has not made that claim yet — saying so is the difference between
              "nothing before block 8,075" and "nothing, as far as we looked". */}
          <p className="panel-note">
            Requests and results together: an <strong>extrinsic</strong> is what somebody asked the
            chain to do, an event is what it then reported. Both decoded against the runtime that
            produced each block.
            {detail.indexed_from !== null && detail.indexed_to !== null && (
              <>
                {' '}
                The observer has read blocks {fmtHeight(detail.indexed_from)} to{' '}
                {fmtHeight(detail.indexed_to)}
                {detail.indexed_from > 1 && ' and is still working back toward genesis'}.
              </>
            )}
            {detail.activity.length >= 100 && ' Showing the most recent 100 events.'}
          </p>
        </>
      )}
    </section>
  )
}
